import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import type { CardLens, LensCardData, LensCustomView, LensFilterValues } from '~/types/leLens';

import { lensPrintFontCss } from '../print/lensPrintFonts';

import {
  SNAPSHOT_BOOTSTRAP,
  SNAPSHOT_REQUEST,
  SNAPSHOT_RESPONSE,
  printDocumentInHiddenFrame,
  requestCustomViewSnapshot,
  sanitisePrintDocument,
} from './customLensPrint';

export type CustomLensViewProps = {
  view: LensCustomView;
  lens: CardLens;
  cards: Array<LensCardData>;
  filters: LensFilterValues;
  period: { from?: string; to?: string; summary: string };
  busy?: boolean;
  /** A proposal supplies structure only; it does not load chart results. */
  previewOnly?: boolean;
  /** Set on the Lens print page (scheduled emails). The view sees `state.scheduled` with the
   *  schedule's choices, e.g. `{ includeInternal: false }` to leave internal figures out. */
  scheduled?: { includeInternal: boolean } | null;
  showBridgeStatus?: boolean;
};

export type CustomLensViewHandle = {
  /** Opens the browser's print dialog for the view as the reader sees it (filters, open sections). */
  print: () => Promise<void>;
  /** The same cleaned, script-free document print() prints, for the scheduled-email renderer. */
  snapshot: () => Promise<string>;
};

const escapeScriptEnd = (value: string) => value.replace(/<\/script/gi, '<\\/script');

export function buildCustomLensSourceDocument(view: LensCustomView, channel: string): string {
  const bootstrap = `
(function () {
  var callbacks = [];
  var latest = null;
  window.Lens = Object.freeze({
    ready: function (callback) { if (typeof callback !== 'function') return; callbacks.push(callback); if (latest) callback(latest); },
    state: function () { return latest; },
    card: function (id) { return latest && latest.cards.find(function (card) { return card.cardId === id; }); },
    escape: function (value) { var node = document.createElement('span'); node.textContent = value == null ? '' : String(value); return node.innerHTML; }
  });
  ${SNAPSHOT_BOOTSTRAP}
  window.addEventListener('message', function (event) {
    if (event.source !== window.parent || !event.data || event.data.channel !== '${channel}') return;
    if (event.data.type === '${SNAPSHOT_REQUEST}') {
      var html = '', failed = false;
      try { html = lensSnapshot(); } catch (error) { failed = true; console.error(error); }
      window.parent.postMessage({ type: '${SNAPSHOT_RESPONSE}', channel: '${channel}', requestId: String(event.data.requestId), html: html, error: failed }, '*');
      return;
    }
    if (event.data.type !== 'lens:data') return;
    latest = event.data.payload;
    callbacks.slice().forEach(function (callback) { try { callback(latest); } catch (error) { console.error(error); } });
    window.dispatchEvent(new CustomEvent('lens:data', { detail: latest }));
  });
})();`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; connect-src 'none'; img-src data: blob:; media-src data: blob:; font-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; object-src 'none'; frame-src 'none'; child-src 'none'; worker-src 'none'; base-uri 'none'; form-action 'none'"><style>${view.css ?? ''}</style></head><body>${view.html}<script>${escapeScriptEnd(bootstrap)}</script><script>${escapeScriptEnd(view.javascript ?? '')}</script></body></html>`;
}

export function buildCustomLensPayload(
  lens: CardLens,
  cards: Array<LensCardData>,
  filters: LensFilterValues,
  period: { from?: string; to?: string; summary: string },
  scheduled: { includeInternal: boolean } | null = null,
) {
  return {
    lens: { id: lens.id, title: lens.title, description: lens.description, version: lens.version },
    state: scheduled
      ? { filters, period, scheduled: { includeInternal: scheduled.includeInternal === true } }
      : { filters, period },
    cards: cards.map(({ query: _query, ...result }) => ({
      ...result,
      title: lens.spec.cards.find((card) => card.id === result.cardId)?.title ?? result.cardId,
    })),
  };
}

const CustomLensView = forwardRef<CustomLensViewHandle, CustomLensViewProps>(function CustomLensView(
  { view, lens, cards, filters, period, busy = false, previewOnly = false, scheduled = null, showBridgeStatus = true },
  ref,
) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const channelRef = useRef(uuidv4());
  const source = useMemo(() => buildCustomLensSourceDocument(view, channelRef.current), [view]);
  const candidatePayload = useMemo(
    () => buildCustomLensPayload(lens, cards, filters, period, scheduled),
    [cards, filters, lens, period, scheduled],
  );
  // The page recreates the period object when its menu/progress state changes. Keep equal
  // bridge data stable so preparing print cannot resend data and reset reader controls.
  const payloadKey = JSON.stringify(candidatePayload);
  const stablePayloadRef = useRef({ key: payloadKey, value: candidatePayload });
  if (stablePayloadRef.current.key !== payloadKey) {
    stablePayloadRef.current = { key: payloadKey, value: candidatePayload };
  }
  const payload = stablePayloadRef.current.value;

  const printAbortRef = useRef<AbortController | null>(null);
  const printStateRef = useRef({ source, payload, busy });
  printStateRef.current = { source, payload, busy };
  useEffect(() => () => printAbortRef.current?.abort(), [source, payload, busy]);

  const send = () => {
    frameRef.current?.contentWindow?.postMessage({ type: 'lens:data', channel: channelRef.current, payload }, '*');
  };

  useEffect(send, [payload, source]);

  // The cleaned print copy of the view as it is now. print() sends it to the print dialog; the
  // Lens print page (scheduled emails) hands it to svc-reporting, which prints it with scripts off.
  const takeSnapshot = async (controller: AbortController) => {
    const frame = frameRef.current;
    if (!frame) {
      throw new Error('This custom view has no HTML to print.');
    }
    const state = printStateRef.current;
    const snapshot = await requestCustomViewSnapshot(frame, channelRef.current, undefined, controller.signal);
    const current = printStateRef.current;
    if (
      controller.signal.aborted ||
      current.busy ||
      current.source !== state.source ||
      current.payload !== state.payload
    ) {
      throw new Error('The view changed while preparing print. Try again.');
    }
    const fontCss = /Suisse/.test(snapshot) ? await lensPrintFontCss() : '';
    const latest = printStateRef.current;
    if (
      controller.signal.aborted ||
      latest.busy ||
      latest.source !== state.source ||
      latest.payload !== state.payload
    ) {
      throw new Error('The view changed while preparing print. Try again.');
    }
    return sanitisePrintDocument(snapshot, lens.title, fontCss);
  };

  useImperativeHandle(
    ref,
    () => ({
      snapshot: async () => {
        if (busy || printAbortRef.current) {
          throw new Error('The view is still updating or preparing print. Try again in a moment.');
        }
        const controller = new AbortController();
        printAbortRef.current = controller;
        try {
          return await takeSnapshot(controller);
        } finally {
          printAbortRef.current = null;
        }
      },
      print: async () => {
        const frame = frameRef.current;
        if (!frame) {
          throw new Error('This custom view has no HTML to print.');
        }
        if (busy || printAbortRef.current) {
          throw new Error('The view is still updating or preparing print. Try again in a moment.');
        }
        const controller = new AbortController();
        printAbortRef.current = controller;
        try {
          await printDocumentInHiddenFrame(await takeSnapshot(controller), document, controller.signal);
        } finally {
          printAbortRef.current = null;
        }
      },
    }),
    [lens.title, busy],
  );

  return (
    <Box>
      {showBridgeStatus && (
        <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Chip
              color="success"
              label={previewOnly ? 'Layout preview' : busy ? 'Refreshing live data' : 'Live data bridge'}
              size="small"
              variant="outlined"
            />
            <Typography color="text.secondary" variant="caption">
              Sandboxed · no network or Admin access
            </Typography>
          </Stack>
          <Typography color="text.secondary" variant="caption">
            {previewOnly
              ? 'Chart results are omitted'
              : `${cards.length} ${cards.length === 1 ? 'result' : 'results'} supplied`}
          </Typography>
        </Stack>
      )}
      {view.html.trim() ? (
        <Box sx={{ position: 'relative' }}>
          <Box
            component="iframe"
            ref={frameRef}
            onLoad={send}
            sandbox="allow-scripts"
            srcDoc={source}
            title={`${lens.title} custom view`}
            sx={{
              bgcolor: '#f6f1e8',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              display: 'block',
              height: Math.min(2400, Math.max(320, view.height ?? 760)),
              width: '100%',
            }}
          />
          {busy && !previewOnly && (
            <Stack
              alignItems="center"
              aria-busy
              justifyContent="center"
              role="status"
              sx={{ bgcolor: 'background.default', borderRadius: '8px', inset: 0, position: 'absolute' }}
            >
              <CircularProgress aria-label="Updating" size={20} />
            </Stack>
          )}
        </Box>
      ) : (
        <Alert severity="info">This custom view has no HTML yet.</Alert>
      )}
    </Box>
  );
});

export default CustomLensView;
